import React, { useState, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  Table,
  Button,
  Row,
  Col,
  Collapse,
  List,
  Typography,
  Popover,
  Dropdown,
  Menu,
  notification,
} from 'antd'
import {
  RetweetOutlined,
  ExceptionOutlined,
  RedoOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { map } from 'lodash-es'
import { Panel } from '../../../core/components/styled'
import {
  createUrl,
  getQueryParams,
} from '../../../core/utils/services/queryParams'
import { formatDate } from '../../../core/utils/services/formatter'
import { timeBetween } from '../../../core/utils/services/date-utils'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { useDocumentTitle } from '../../../common/hooks/effects'
import { formatScrapingTags } from '../../utils'
import { SCRAPING_SESSIONS } from '../../graphql/queries'

import {
  UsersFilter,
  ScrapingSessionStatusFilter,
  ScrapingTypeFilter,
  ScrapingSessionStatus,
  ScrapingSessionLogsModal,
  ScrapingSessionSettingsJsonView,
} from './sections'
import { START_SCRAPING_SESSION_DUPLICATE } from '../../graphql/mutations'
import {
  HTML_AUTOMATIC,
  IN_PROGRESS,
  SCHEDULED,
  SCRAPING_TYPES,
} from '../../constants'

const ScrapingSessionsHistoryPage = () => {
  useDocumentTitle('Scraping sessions history')

  const [logsModalSettings, setLogsModalSettings] = useState({
    sessionId: undefined,
    menuId: undefined,
    isModalOpen: false,
  })

  const [
    startingScrapingSessionForId,
    setStartingScrapingSessionForId,
  ] = useState()

  const history = useHistory()
  const { pathname, search } = useLocation()
  const { userIds, status, scrapingType, page = DEFAULT_PAGE } = getQueryParams(
    search,
  )

  const {
    data: { scrapingSessions = {} } = {},
    loading: isLoadingSessions,
    refetch: refetchScrapingSessions,
  } = useQuery(SCRAPING_SESSIONS, {
    variables: {
      filters: { userIds, status, scrapingType },
      page,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const [startScrapingSessionDuplicate] = useMutation(
    START_SCRAPING_SESSION_DUPLICATE,
    {
      onCompleted() {
        refetchScrapingSessions()
        setStartingScrapingSessionForId()
        notification.open({
          message: 'Started scraping session.',
          type: 'success',
        })
      },
      onError({ message }) {
        notification.open({
          message: 'Failed to start scraping session.',
          description: message,
          type: 'error',
          duration: 0,
        })
      },
    },
  )

  const startScrapingSessionWithDefaultModeScraping = useCallback(
    scrapingSessionId => {
      setStartingScrapingSessionForId(scrapingSessionId)

      startScrapingSessionDuplicate({
        variables: {
          id: scrapingSessionId,
          isDebugModeScraping: false,
        },
      })
    },
    [startScrapingSessionDuplicate],
  )

  const startScrapingSessionWithDebugModeScraping = useCallback(
    scrapingSessionId => {
      setStartingScrapingSessionForId(scrapingSessionId)

      startScrapingSessionDuplicate({
        variables: {
          id: scrapingSessionId,
          isDebugModeScraping: true,
        },
      })
    },
    [startScrapingSessionDuplicate],
  )

  const rerunButtonMenu = scrapingSession => (
    <Menu
      onClick={() =>
        startScrapingSessionWithDebugModeScraping(scrapingSession.id)
      }
    >
      <Menu.Item>Rerun in debug mode</Menu.Item>
    </Menu>
  )

  const isSessionRunning = scrapingSession =>
    scrapingSession.status === IN_PROGRESS ||
    scrapingSession.status === SCHEDULED

  const renderRerunButton = scrapingSession => {
    if (scrapingSession.id === startingScrapingSessionForId) {
      return <Button style={{ width: '75px' }} icon={<LoadingOutlined />} />
    }

    if (scrapingSession.scrapingType === HTML_AUTOMATIC) {
      return (
        <Button
          style={{ width: '75px' }}
          disabled={isSessionRunning(scrapingSession)}
          icon={<RedoOutlined />}
          onClick={() =>
            startScrapingSessionWithDefaultModeScraping(scrapingSession.id)
          }
        />
      )
    }

    return (
      <Dropdown.Button
        style={{ width: '75px' }}
        disabled={isSessionRunning(scrapingSession)}
        onClick={() =>
          startScrapingSessionWithDefaultModeScraping(scrapingSession.id)
        }
        overlay={() => rerunButtonMenu(scrapingSession)}
      >
        <RedoOutlined />
      </Dropdown.Button>
    )
  }

  const tableColumns = [
    {
      title: 'Status',
      dataIndex: 'status',
      width: '120px',
      render: status => <ScrapingSessionStatus status={status} />,
    },
    {
      title: 'Progress',
      width: '50px',
      render: scrapingSession =>
        `${scrapingSession.scrapingTestCaseResultCount}/${scrapingSession.scrapingTestCaseCount}`,
    },
    {
      title: 'Started by',
      dataIndex: 'createdBy',
      width: '150px',
      render: user => user?.name || 'System',
    },
    {
      title: 'Started at',
      dataIndex: 'startedAt',
      width: '120px',
      render: startedAt => formatDate(startedAt),
    },
    {
      title: 'Completed at',
      dataIndex: 'completedAt',
      width: '130px',
      render: completedAt => formatDate(completedAt),
    },
    {
      title: 'Duration',
      width: '140px',
      render: scrapingSession =>
        scrapingSession.completedAt &&
        timeBetween(scrapingSession.startedAt, scrapingSession.completedAt),
    },
    {
      title: 'Logs',
      width: '50px',
      render: scrapingSession => (
        <Button
          icon={<ExceptionOutlined />}
          onClick={event => {
            event.stopPropagation()
            setLogsModalSettings({
              sessionId: scrapingSession.id,
              isModalOpen: true,
            })
          }}
        />
      ),
    },
    {
      title: 'Rerun',
      width: '50px',
      render: renderRerunButton,
    },
    {
      title: 'Tag groups',
      dataIndex: 'tagGroups',
      width: '400px',
      render: tagGroups =>
        map(tagGroups, (tagGroup, index) => (
          <Collapse key={index} defaultActiveKey={['1']}>
            <Collapse.Panel header={tagGroup.alias || tagGroup.name}>
              <List
                size="small"
                split={false}
                dataSource={formatScrapingTags(tagGroup.tags)}
                itemLayout="horizontal"
                renderItem={tag => (
                  <List.Item>
                    <Typography.Text>{tag}</Typography.Text>
                  </List.Item>
                )}
              />
            </Collapse.Panel>
          </Collapse>
        )),
    },
    {
      title: 'Scraping Type',
      dataIndex: 'scrapingType',
      width: '135px',
      render: scrapingType =>
        SCRAPING_TYPES[scrapingType]?.name || scrapingType,
    },
    {
      title: 'Session Settings',
      dataIndex: 'scrapingSettings',
      width: '300px',
      render: scrapingSettings => (
        <ScrapingSessionSettingsJsonView scrapingSettings={scrapingSettings} />
      ),
    },
    {
      title: () => (
        <Popover trigger="hover" content="Refetch sessions">
          <Button
            style={{ marginRight: '15px' }}
            icon={<RetweetOutlined />}
            onClick={() => refetchScrapingSessions()}
          />
        </Popover>
      ),
      align: 'right',
    },
  ]

  return (
    <>
      <Panel margin="small no">
        <Row gutter={24}>
          <Col span={3}>
            <ScrapingSessionStatusFilter />
          </Col>
          <Col span={3}>
            <ScrapingTypeFilter />
          </Col>
          <Col span={10}>
            <UsersFilter />
          </Col>
        </Row>
      </Panel>
      <Table
        loading={isLoadingSessions}
        rowKey="id"
        columns={tableColumns}
        dataSource={scrapingSessions.scrapingSessions}
        size="small"
        onRow={scrapingSession => ({
          onClick: event =>
            event.target.tagName === 'TD'
              ? history.push(
                  `/scraping/sessions/management/${scrapingSession.id}`,
                )
              : undefined,
        })}
        pagination={{
          current: page,
          pageSize: DEFAULT_PAGE_SIZE,
          total: scrapingSessions.total,
          onChange: page => history.push(createUrl(pathname, search, { page })),
        }}
      />

      <ScrapingSessionLogsModal
        {...logsModalSettings}
        closeModal={() =>
          setLogsModalSettings({
            scrapingSessionId: undefined,
            isModalOpen: false,
          })
        }
      />
    </>
  )
}

export default ScrapingSessionsHistoryPage
